import {
  isServiceExchange,
  marketKindOf,
  type ServiceExchange,
  type ServiceMarketKind,
} from "./exchanges";

/** A live price from the price service. */
export interface PriceUpdate {
  exchange: ServiceExchange;
  symbol: string;
  market: ServiceMarketKind;
  /** Mark price when the venue has one, else the last trade price. */
  markPrice: number;
  lastPrice: number | null;
  /** Unix ms. */
  timestamp: number;
  /** Funding of a futures market; null on spot or when the venue sent none. */
  funding: FundingInfo | null;
}

/** A perpetual's funding as the price service reports it. */
export interface FundingInfo {
  /** Current (predicted) rate per funding period, as a fraction (0.0001 = 0.01%). */
  rate: number;
  /** Unix ms of the next funding payment, when known. */
  nextFundingTime: number | null;
  /** Hours between payments, when known. */
  intervalHours: number | null;
}

/** A candle from the price service; `time` is unix seconds. */
export interface ServiceCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

/** Price row/message fields shared by `/v1/prices` and the stream. */
export interface RawServicePrice {
  exchange?: unknown;
  symbol?: unknown;
  market?: unknown;
  price?: unknown;
  last?: unknown;
  mark?: unknown;
  ts?: unknown;
  age_seconds?: unknown;
  funding_rate?: unknown;
  next_funding_ts?: unknown;
  funding_interval_hours?: unknown;
}

/** A price older than this is no price (the venue stopped ticking). */
export const MAX_PRICE_AGE_MS = 10 * 60 * 1000;

/** True when a price stamped `timestamp` (unix ms) is too old to show. */
export function isStalePrice(timestamp: number, now = Date.now()): boolean {
  return now - timestamp > MAX_PRICE_AGE_MS;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/** The funding fields of a price row, or null without a rate. */
export function toFundingInfo(raw: RawServicePrice): FundingInfo | null {
  if (raw.funding_rate === null || raw.funding_rate === undefined || raw.funding_rate === "") {
    return null;
  }
  const rate = Number(raw.funding_rate);
  if (!Number.isFinite(rate)) return null;
  return {
    rate,
    nextFundingTime: toNumber(raw.next_funding_ts),
    intervalHours: toNumber(raw.funding_interval_hours),
  };
}

export interface ToPriceUpdateOptions {
  /**
   * Without `age_seconds`, judge the age from `ts` against this device's
   * clock. REST snapshots set it; live stream pushes are current by
   * definition, and a phone clock that is off would hide every one of them.
   */
  checkTs?: boolean;
  now?: number;
}

/**
 * A price row or stream message as a PriceUpdate, or null when it has no
 * price or the price is older than MAX_PRICE_AGE_MS.
 */
export function toPriceUpdate(
  raw: RawServicePrice,
  options: ToPriceUpdateOptions = {}
): PriceUpdate | null {
  const now = options.now ?? Date.now();
  if (typeof raw.exchange !== "string" || !isServiceExchange(raw.exchange)) {
    return null;
  }
  if (typeof raw.symbol !== "string" || !raw.symbol) return null;

  const mark = toNumber(raw.mark);
  const last = toNumber(raw.last);
  const value = mark ?? last ?? toNumber(raw.price);
  if (value === null) return null;

  const market: ServiceMarketKind =
    raw.market === "spot" || raw.market === "futures"
      ? raw.market
      : marketKindOf(raw.exchange);
  const ts = toNumber(raw.ts);
  const age = raw.age_seconds == null ? NaN : Number(raw.age_seconds);
  if (Number.isFinite(age)) {
    if (age * 1000 > MAX_PRICE_AGE_MS) return null;
  } else if (options.checkTs && ts !== null && isStalePrice(ts, now)) {
    return null;
  }

  return {
    exchange: raw.exchange,
    symbol: raw.symbol,
    market,
    markPrice: value,
    lastPrice: last,
    timestamp: ts ?? now,
    funding: market === "futures" ? toFundingInfo(raw) : null,
  };
}

export function toServiceCandle(raw: unknown): ServiceCandle | null {
  if (!raw || typeof raw !== "object") return null;
  const candle = raw as Record<string, unknown>;
  const time = Number(candle.time);
  const open = Number(candle.open);
  const high = Number(candle.high);
  const low = Number(candle.low);
  const close = Number(candle.close);
  if (![time, open, high, low, close].every(Number.isFinite)) return null;
  const volume = Number(candle.volume);
  return {
    time,
    open,
    high,
    low,
    close,
    volume: Number.isFinite(volume) ? volume : 0,
  };
}
